import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Sparkles, ArrowRight, ArrowLeft, Pencil, Check, Calendar as CalendarIcon,
} from 'lucide-react';
import { format, parse, isValid } from 'date-fns';

import AuthModal from '@/components/AuthModal';
import { PageShell } from '@/components/brand/PageShell';
import { LoadingState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { STAGE_EXPECTING, STAGE_NEWBORN, imgProps } from '@/lib/imagery';
import { capture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Field primitives                                                           */
/* -------------------------------------------------------------------------- */

const fieldInput =
  'w-full h-12 px-4 rounded-xl bg-cream-50 border border-border placeholder:text-ink-muted/70 text-ink ' +
  'text-[15px] focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 transition';

const Field = ({ label, hint, error, required, children, srOnlyLabel }) => (
  <label className="block">
    <span
      className={
        srOnlyLabel
          ? 'sr-only'
          : 'block text-[13px] font-medium text-ink-soft mb-1.5'
      }
    >
      {label}{required && !srOnlyLabel && <span className="text-clay-600 ml-0.5">*</span>}
    </span>
    {children}
    {error ? (
      <span className="block text-xs text-clay-700 mt-1.5">{error}</span>
    ) : hint ? (
      <span className="block text-xs text-ink-muted mt-1.5 leading-snug">{hint}</span>
    ) : null}
  </label>
);

/* -------------------------------------------------------------------------- */
/*  DateField — Popover + Calendar with "dd mmm yyyy" placeholder              */
/* -------------------------------------------------------------------------- */
/* Stores `value` as a YYYY-MM-DD string (preserves the API contract);          */
/* renders the selected date as "12 Mar 2026", or the placeholder when empty.   */

const DateField = ({
  value,
  onChange,
  placeholder = 'dd mmm yyyy',
  invalid,
  ariaLabel,
  testId,
  fromYear,
  toYear,
}) => {
  const [open, setOpen] = React.useState(false);
  const dateObj = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  const formatted = dateObj && isValid(dateObj) ? format(dateObj, 'dd MMM yyyy') : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-invalid={!!invalid}
          data-testid={testId}
          className={`w-full h-12 px-4 rounded-xl bg-cream-50 border text-left text-[15px]
            flex items-center justify-between gap-3 transition
            ${invalid ? 'border-clay-400' : 'border-border'}
            hover:border-clay-300 focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300`}
        >
          <span className={formatted ? 'text-ink' : 'text-ink-muted/70'}>
            {formatted || placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 text-ink-muted shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-cream-50 border-border/70 rounded-2xl shadow-soft-2"
      >
        <Calendar
          mode="single"
          selected={dateObj || undefined}
          onSelect={(d) => {
            onChange(d ? format(d, 'yyyy-MM-dd') : '');
            setOpen(false);
          }}
          captionLayout={fromYear || toYear ? 'dropdown-buttons' : undefined}
          fromYear={fromYear}
          toYear={toYear}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

/* -------------------------------------------------------------------------- */
/*  Chips — radio-style selection                                              */
/* -------------------------------------------------------------------------- */

const Chip = ({ active, onClick, label, hint, fullWidth }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex flex-col items-start text-left gap-0.5 px-5 py-4 rounded-2xl border transition-all
      ${fullWidth ? 'w-full' : ''}
      ${active
        ? 'border-clay-500 bg-clay-50/60 shadow-soft-1 ring-1 ring-clay-200'
        : 'border-border hover:border-clay-300 bg-cream-50'}`}
    aria-pressed={active}
  >
    <span className="text-[14.5px] font-medium text-ink leading-tight">{label}</span>
    {hint && <span className="text-[12.5px] text-ink-muted">{hint}</span>}
    {active && (
      <span className="absolute top-3 right-3 inline-flex h-5 w-5 rounded-full bg-clay-600 text-cream-50 items-center justify-center">
        <Check className="h-3 w-3" />
      </span>
    )}
  </button>
);

/* -------------------------------------------------------------------------- */
/*  Tier options for the curate-for-me path                                    */
/* -------------------------------------------------------------------------- */

const tierOptions = [
  { id: 'budget',  name: 'Considered',  hint: '~₹50K · gentle picks' },
  { id: 'premium', name: 'Considered+', hint: '~₹80K · brand-led' },
  { id: 'luxury',  name: 'Heirloom',    hint: '₹1L+ · pieces to keep' },
  { id: 'all',     name: 'A mix',       hint: 'A balanced range' },
];

/* -------------------------------------------------------------------------- */
/*  Step indicator                                                             */
/* -------------------------------------------------------------------------- */

const StepIndicator = ({ steps, current, onJump }) => {
  const progress = ((current + 1) / steps.length) * 100;
  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[12px] uppercase tracking-[0.16em] text-ink-muted">
          Step {current + 1} of {steps.length}
        </p>
        <p className="text-[13px] text-ink-soft">{steps[current].label}</p>
      </div>
      <div className="h-1 bg-cream-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-clay-500 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Dot rail — clickable for completed steps */}
      <div className="flex items-center gap-1.5 pt-1">
        {steps.map((s, i) => (
          <button
            key={s.id}
            onClick={() => i < current && onJump?.(i)}
            disabled={i >= current}
            className={`h-1.5 flex-1 rounded-full transition-colors
              ${i < current ? 'bg-clay-500/70 hover:bg-clay-500 cursor-pointer'
                : i === current ? 'bg-clay-500'
                : 'bg-cream-200'}`}
            aria-label={`Step ${i + 1}: ${s.label}`}
          />
        ))}
      </div>
    </div>
  );
};

/* ========================================================================== */
/*                                   PAGE                                     */
/* ========================================================================== */

const RegistryOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();

  const eventType = location.state?.eventType || 'Baby';

  /* ---------- core state ---------- */
  const [showAuth, setShowAuth] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiItems, setAiItems] = useState([]);
  const [showAIBasket, setShowAIBasket] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    delivery_address: '',
    event_type: eventType,
    event_date: '',
    event_details: {
      city: '', due_date: '', gender: 'neutral', parents_age: '',
      wedding_date: '', partner_names: '', age: '',
    },
    preferences: {
      budget_conscious: false, eco_friendly: false, premium_brands: false, favorite_colors: [],
    },
    tier: 'all',
    privacy_level: 'public',
    /* local-only, not sent: */
    starter_choice: '',  // 'curate' | 'scratch'
  });

  /* ---------- auth gate + prefill ---------- */
  useEffect(() => {
    if (!user) {
      setShowAuth(true);
    } else {
      setFormData((p) => ({
        ...p,
        name: p.name || user.name || '',
        delivery_address: p.delivery_address || user.address || '',
        event_type: eventType,
        event_details: { ...p.event_details, city: p.event_details.city || user.city || '' },
      }));
      setShowAuth(false);
    }
  }, [user, eventType]);

  /* ---------- helpers ---------- */
  const set = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };
  const setDetail = (field, value) =>
    setFormData((p) => ({ ...p, event_details: { ...p.event_details, [field]: value } }));
  const setPref = (field, value) =>
    setFormData((p) => ({ ...p, preferences: { ...p.preferences, [field]: value } }));

  /* ---------- step config ---------- */
  const steps = [
    { id: 'welcome',    label: 'Welcome'      },
    { id: 'about-you',  label: 'About you'    },
    { id: 'address',    label: 'Delivery'     },
    { id: 'details',    label: eventType === 'Baby' ? 'Little one' : 'Event' },
    { id: 'plan',       label: 'Begin'        },
  ];

  /* ---------- per-step validation ---------- */
  const validateCurrentStep = () => {
    const e = {};
    const id = steps[stepIdx].id;
    if (id === 'about-you') {
      if (!formData.name.trim()) e.name = "What should we call you?";
    }
    if (id === 'address') {
      if (formData.delivery_address.trim().length < 10) e.delivery_address = 'A complete address — house, area, pincode.';
    }
    if (id === 'details') {
      if (!formData.event_date) e.event_date = 'When are you expecting your little one?';
      if (eventType === 'Custom' && !formData.event_details.custom_occasion)
        e.custom_occasion = 'A short name for the occasion.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------- navigation ---------- */
  const goNext = () => {
    if (!validateCurrentStep()) {
      // gentle shake for the focused error — done via re-rendering aria-invalid
      return;
    }
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));
  const jumpTo = (i) => setStepIdx(i);

  /* ---------- API ---------- */
  const generateAIBasket = async () => {
    if (!user || !token) return toast.error('Please sign in first');
    setGeneratingAI(true);
    try {
      const response = await axios.post(
        `${API}/ai/generate-basket`,
        {
          event_type: formData.event_type,
          event_date: formData.event_date,
          event_details: formData.event_details,
          preferences: formData.preferences,
          tier: formData.tier,
          num_items: 12,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiItems(response.data.items || []);
      setShowAIBasket(true);
      const season = response.data.season_analysis || 'this season';
      const loc = response.data.location || 'your location';
      toast.success(`A starter basket, curated for ${season} in ${loc}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate basket');
    } finally {
      setGeneratingAI(false);
    }
  };

  const createRegistry = async (withAIItems = false) => {
    if (!user || !token) return toast.error('Please sign in first');
    if (!formData.name || !formData.delivery_address || !formData.event_date) {
      return toast.error('A few details are missing');
    }
    setLoading(true);
    try {
      const registryResponse = await axios.post(`${API}/registry/create`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const registryId = registryResponse.data.id;

      if (withAIItems && aiItems.length > 0) {
        await axios.put(`${API}/registry/${registryId}/items`, aiItems, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      capture('registry_created', { with_ai_items: Boolean(withAIItems && aiItems.length > 0) });
      toast.success('Your basket is ready.');
      navigate(`/registry/${registryId}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create registry');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- gate while waiting for auth ---------- */
  if (!user && !showAuth) {
    return <PageShell><LoadingState label="Just a moment…" /></PageShell>;
  }

  /* ---------- AI results view (after curate) ---------- */
  if (showAIBasket) {
    return (
      <PageShell>
        <div className="container-page py-12 sm:py-16 max-w-3xl">
          <div className="bg-cream-50 border border-border/70 rounded-3xl shadow-soft-1 p-6 sm:p-10 animate-fade-up">
            <div className="flex items-start justify-between gap-4 mb-7">
              <div>
                <div className="eyebrow mb-2">Your starter basket</div>
                <h2 className="font-display text-display-md text-ink leading-snug">
                  Curated for you,{' '}
                  <span className="italic text-clay-700">to begin with.</span>
                </h2>
                <p className="mt-2 text-ink-soft max-w-md">
                  {aiItems.length} thoughtful picks. You can change everything later — add, remove, swap.
                </p>
              </div>
              <button
                onClick={() => setShowAIBasket(false)}
                className="text-sm text-ink-muted hover:text-clay-700 inline-flex items-center gap-1 shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {aiItems.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl border border-border/70 bg-cream-50 p-5 shadow-soft-1"
                  data-testid={`ai-item-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-base text-ink leading-snug">{item.name}</h3>
                    {item.priority && (
                      <span className="shrink-0 text-[11px] uppercase tracking-[0.16em] text-clay-700 bg-clay-50 border border-clay-100 px-2 py-1 rounded-full">
                        {item.priority}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="mt-1.5 text-sm text-ink-muted leading-snug line-clamp-2">{item.description}</p>
                  )}
                  {item.price && (
                    <p className="mt-2.5 font-display text-lg text-ink">₹{item.price}</p>
                  )}
                  {item.why_suggested && (
                    <p className="mt-3 text-[12.5px] text-ink-soft italic leading-snug border-l-2 border-clay-200 pl-3">
                      {item.why_suggested}
                    </p>
                  )}
                </article>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <BrandButton
                size="lg"
                onClick={() => createRegistry(true)}
                disabled={loading}
                loading={loading}
                data-testid="accept-ai-basket-btn"
                className="flex-1"
              >
                Accept this basket
                <ArrowRight className="h-4 w-4" />
              </BrandButton>
              <BrandButton variant="outline" size="lg" onClick={() => setShowAIBasket(false)}>
                Curate again
              </BrandButton>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  /* ---------- main multi-step view ---------- */

  const stepId = steps[stepIdx].id;

  return (
    <PageShell>
      <div className="container-page py-10 sm:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left rail — image + reassurance (sticky on desktop) */}
          <aside className="hidden lg:block lg:col-span-5 sticky top-24 self-start">
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-soft-2">
              <img
                {...imgProps(stepIdx < 2 ? STAGE_EXPECTING : STAGE_NEWBORN)}
                className="h-full w-full object-cover transition-opacity duration-700"
                key={stepIdx < 2 ? 'expecting' : 'newborn'}
              />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent">
                <p className="font-display text-cream-50 text-xl leading-snug max-w-xs">
                  {stepIdx === 0 && '"It made the lead-up less anxious."'}
                  {stepIdx === 1 && '"Take your time — we\'ll save everything as you go."'}
                  {stepIdx === 2 && '"This is just where the gifts will land."'}
                  {stepIdx === 3 && '"You can change everything later. Promise."'}
                  {stepIdx === 4 && '"You\'re a few seconds from a basket of your own."'}
                </p>
              </div>
            </div>
          </aside>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <div className="bg-cream-50 border border-border/70 rounded-3xl shadow-soft-1 p-6 sm:p-10">
              <StepIndicator steps={steps} current={stepIdx} onJump={jumpTo} />

              {/* Animated step body */}
              <div key={stepId} className="mt-9 animate-fade-up">
                {/* ====================== Step 1: Welcome ====================== */}
                {stepId === 'welcome' && (
                  <div>
                    <LogoMark size={32} className="text-clay-700 mb-6" />
                    <h1 className="font-display text-display-lg text-ink leading-[1.05]">
                      Welcome.{' '}
                      <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
                        Let's begin your basket.
                      </span>
                    </h1>
                    <p className="mt-5 text-lg text-ink-soft max-w-md leading-relaxed">
                      A few quiet details — about two minutes, no rush.
                      We'll save what you've answered as you go, so you can leave and come back anytime.
                    </p>

                    <ul className="mt-7 space-y-3 text-[14.5px] text-ink-soft">
                      {[
                        ['Free, always',           'For you and every guest.'],
                        ['Private until you share',  "Your basket stays just yours until you're ready."],
                        ['Change anything, anytime', 'Nothing here is final.'],
                      ].map(([t, b]) => (
                        <li key={t} className="flex gap-3">
                          <span className="mt-1 inline-flex shrink-0 h-5 w-5 rounded-full bg-clay-100 text-clay-700 items-center justify-center">
                            <Check className="h-3 w-3" />
                          </span>
                          <span><span className="text-ink font-medium">{t}.</span> {b}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-9">
                      <BrandButton size="lg" onClick={goNext} data-testid="welcome-begin">
                        Let's begin
                        <ArrowRight className="h-4 w-4" />
                      </BrandButton>
                    </div>
                  </div>
                )}

                {/* ====================== Step 2: About you ====================== */}
                {stepId === 'about-you' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      What should we call you?
                    </h1>
                    <p className="mt-2 text-ink-soft">
                      Just a first name is fine — your guests will see it on your registry.
                    </p>

                    <div className="mt-8 space-y-5">
                      <Field label="Your name" required error={errors.name}>
                        <Input
                          autoFocus
                          value={formData.name}
                          onChange={(e) => set('name', e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && goNext()}
                          placeholder="Aanya"
                          className={fieldInput}
                          data-testid="name-input"
                          aria-invalid={!!errors.name}
                        />
                      </Field>

                      <Field label="Your age" hint="Optional. Helps us tailor a few suggestions.">
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={formData.age}
                          onChange={(e) => set('age', e.target.value)}
                          placeholder="32"
                          className={fieldInput}
                          data-testid="age-input"
                        />
                      </Field>
                    </div>
                  </div>
                )}

                {/* ====================== Step 3: Address ====================== */}
                {stepId === 'address' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      Where should we send your gifts?
                    </h1>
                    <p className="mt-2 text-ink-soft">
                      The address gift-givers will see when they check out. You can update it anytime.
                    </p>

                    <div className="mt-8 space-y-5">
                      <Field label="Delivery address" required error={errors.delivery_address}>
                        <textarea
                          rows={3}
                          value={formData.delivery_address}
                          onChange={(e) => set('delivery_address', e.target.value)}
                          placeholder="House / flat, street, area, city, pincode"
                          className={`${fieldInput} h-auto py-3 leading-relaxed resize-none`}
                          data-testid="delivery-address-input"
                          aria-invalid={!!errors.delivery_address}
                        />
                      </Field>

                      <Field label="City" hint="So we can suggest things from nearby brands">
                        <Input
                          value={formData.event_details.city}
                          onChange={(e) => setDetail('city', e.target.value)}
                          placeholder="Bangalore"
                          className={fieldInput}
                        />
                      </Field>
                    </div>

                    <p className="mt-5 text-xs text-ink-muted">
                      No worries — only you and people with your registry link will ever see this.
                    </p>
                  </div>
                )}

                {/* ====================== Step 4: Little one (or event) ====================== */}
                {stepId === 'details' && eventType === 'Baby' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      When are you expecting your little one?
                    </h1>
                    <p className="mt-2 text-ink-soft">
                      Approximate is fine — we'll quietly tailor what we suggest.
                    </p>

                    <div className="mt-8 space-y-6">
                      <Field label="Expected arrival" required error={errors.event_date}>
                        <DateField
                          value={formData.event_date}
                          onChange={(v) => set('event_date', v)}
                          ariaLabel="Expected arrival date"
                          testId="event-date-input"
                          invalid={!!errors.event_date}
                          fromYear={new Date().getFullYear()}
                          toYear={new Date().getFullYear() + 2}
                        />
                      </Field>

                      <div>
                        <p className="text-[13px] font-medium text-ink-soft mb-2">
                          Are you expecting a…
                        </p>
                        <div className="grid grid-cols-3 gap-2.5" role="radiogroup" data-testid="gender-select">
                          {[
                            { v: 'neutral', label: 'A surprise',  hint: 'or neutral picks' },
                            { v: 'boy',     label: 'A boy',        hint: 'tailor a little' },
                            { v: 'girl',    label: 'A girl',       hint: 'tailor a little' },
                          ].map((c) => (
                            <Chip
                              key={c.v}
                              active={formData.event_details.gender === c.v}
                              onClick={() => setDetail('gender', c.v)}
                              label={c.label}
                              hint={c.hint}
                              fullWidth
                            />
                          ))}
                        </div>
                        <p className="text-xs text-ink-muted mt-2.5">
                          We won't gender your registry — this just nudges a couple of suggestions.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Wedding / Birthday / Custom variants */}
                {stepId === 'details' && eventType === 'Wedding' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      Tell us about the day.
                    </h1>
                    <p className="mt-2 text-ink-soft">A small note helps us set the table right.</p>
                    <div className="mt-8 space-y-5">
                      <Field label="The wedding date" required error={errors.event_date}>
                        <DateField
                          value={formData.event_date}
                          onChange={(v) => set('event_date', v)}
                          ariaLabel="Wedding date"
                          invalid={!!errors.event_date}
                        />
                      </Field>
                      <Field label="The couple">
                        <Input value={formData.event_details.partner_names} onChange={(e) => setDetail('partner_names', e.target.value)} placeholder="Priya & Raj" className={fieldInput} />
                      </Field>
                      <Field label="A theme" hint="Optional">
                        <Input value={formData.event_details.wedding_theme || ''} onChange={(e) => setDetail('wedding_theme', e.target.value)} placeholder="Traditional, modern, beachside…" className={fieldInput} />
                      </Field>
                    </div>
                  </div>
                )}

                {stepId === 'details' && eventType === 'Birthday' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      Tell us about the birthday.
                    </h1>
                    <div className="mt-8 space-y-5">
                      <Field label="The day" required error={errors.event_date}>
                        <DateField
                          value={formData.event_date}
                          onChange={(v) => set('event_date', v)}
                          ariaLabel="Birthday date"
                          invalid={!!errors.event_date}
                        />
                      </Field>
                      <Field label="Age">
                        <Input type="number" value={formData.event_details.age} onChange={(e) => setDetail('age', e.target.value)} placeholder="5" className={fieldInput} />
                      </Field>
                      <div>
                        <p className="text-[13px] font-medium text-ink-soft mb-2">For…</p>
                        <div className="grid grid-cols-3 gap-2.5">
                          {[['neutral', 'Prefer not to say'], ['male', 'A boy'], ['female', 'A girl']].map(([v, l]) => (
                            <Chip key={v} active={(formData.event_details.birthday_gender || 'neutral') === v} onClick={() => setDetail('birthday_gender', v)} label={l} fullWidth />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {stepId === 'details' && eventType === 'Custom' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      Tell us about the occasion.
                    </h1>
                    <div className="mt-8 space-y-5">
                      <Field label="The day" required error={errors.event_date}>
                        <DateField
                          value={formData.event_date}
                          onChange={(v) => set('event_date', v)}
                          ariaLabel="Event date"
                          invalid={!!errors.event_date}
                        />
                      </Field>
                      <Field label="Occasion" required error={errors.custom_occasion}>
                        <Input value={formData.event_details.custom_occasion || ''} onChange={(e) => setDetail('custom_occasion', e.target.value)} placeholder="Housewarming, anniversary, graduation…" className={fieldInput} />
                      </Field>
                      <Field label="A line about it" hint="Optional">
                        <Input value={formData.event_details.custom_description || ''} onChange={(e) => setDetail('custom_description', e.target.value)} placeholder="A small dinner, just close friends." className={fieldInput} />
                      </Field>
                    </div>
                  </div>
                )}

                {/* ====================== Step 5: Plan ====================== */}
                {stepId === 'plan' && (
                  <div>
                    <h1 className="font-display text-display-md text-ink leading-snug">
                      How would you like to begin?
                    </h1>
                    <p className="mt-2 text-ink-soft">
                      Either way, you can edit everything later. Just two paths in.
                    </p>

                    <div className="mt-7 grid grid-cols-1 gap-3">
                      <Chip
                        active={formData.starter_choice === 'curate'}
                        onClick={() => set('starter_choice', 'curate')}
                        label="Curate a starter basket for me"
                        hint="A dozen thoughtful picks, tuned to your details. Editable in two clicks."
                        fullWidth
                      />
                      <Chip
                        active={formData.starter_choice === 'scratch'}
                        onClick={() => set('starter_choice', 'scratch')}
                        label="I'll add things myself"
                        hint="Empty basket, ready for you to fill from any shop."
                        fullWidth
                      />
                    </div>

                    {/* Tier picker — only when curate is chosen */}
                    {formData.starter_choice === 'curate' && (
                      <div className="mt-6 pt-6 border-t border-border/60 animate-fade-up">
                        <p className="text-[13px] font-medium text-ink-soft mb-3">
                          What feels right for you?
                        </p>
                        <div className="grid grid-cols-2 gap-2.5">
                          {tierOptions.map((opt) => (
                            <Chip
                              key={opt.id}
                              active={formData.tier === opt.id}
                              onClick={() => set('tier', opt.id)}
                              label={opt.name}
                              hint={opt.hint}
                              fullWidth
                            />
                          ))}
                        </div>
                        <label className="mt-4 flex items-center gap-3 cursor-pointer">
                          <Checkbox
                            checked={formData.preferences.eco_friendly}
                            onCheckedChange={(c) => setPref('eco_friendly', c)}
                            data-testid="eco-checkbox"
                          />
                          <span>
                            <span className="block text-sm text-ink font-medium">Lean eco-friendly</span>
                            <span className="block text-xs text-ink-muted">Materials and brands with kinder sourcing</span>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer nav — appears on every step except welcome */}
              {stepId !== 'welcome' && (
                <div className="mt-10 pt-6 border-t border-border/60 flex items-center justify-between gap-3">
                  <BrandButton variant="ghost" onClick={goBack} disabled={loading || generatingAI}>
                    <ArrowLeft className="h-4 w-4" /> Back
                  </BrandButton>

                  {stepId === 'plan' ? (
                    <div className="flex flex-col sm:flex-row gap-2.5">
                      {formData.starter_choice === 'curate' && (
                        <BrandButton
                          size="lg"
                          onClick={generateAIBasket}
                          disabled={generatingAI}
                          loading={generatingAI}
                          data-testid="ai-basket-btn"
                        >
                          <Sparkles className="h-4 w-4" />
                          {generatingAI ? 'Curating…' : 'Curate my basket'}
                        </BrandButton>
                      )}
                      {formData.starter_choice === 'scratch' && (
                        <BrandButton
                          size="lg"
                          onClick={() => createRegistry(false)}
                          disabled={loading}
                          loading={loading}
                          data-testid="create-own-basket-btn"
                        >
                          <Pencil className="h-4 w-4" />
                          Create my basket
                        </BrandButton>
                      )}
                      {!formData.starter_choice && (
                        <BrandButton size="lg" disabled>
                          Choose a path above
                        </BrandButton>
                      )}
                    </div>
                  ) : (
                    <BrandButton size="lg" onClick={goNext} disabled={loading}>
                      Continue <ArrowRight className="h-4 w-4" />
                    </BrandButton>
                  )}
                </div>
              )}
            </div>

            <p className="mt-5 text-center text-xs text-ink-muted">
              Saved as you go · Free, always · Private until you share
            </p>
          </div>
        </div>
      </div>

      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => {
            setShowAuth(false);
            if (!user) navigate('/');
          }}
          mode="signup"
        />
      )}
    </PageShell>
  );
};

export default RegistryOnboarding;
